<template>
  <Modal
    v-if="selectedProgramRef && modalStepsRef"
    :on-exit="handleModalExit"
    :no-exit-on-backdrop="isMandatoryRef"
    :modal="menteeMatchingModalRef"
    :steps="modalStepsRef"
  />
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'

import MenteeMatchingConfirmation from './mentee-matching-confirmation/mentee-matching-confirmation.vue'
import MenteeMatchingLoading from './mentee-matching-loading/mentee-matching-loading.vue'

import { RoleEnumTypeV2 } from '@/features/auth/types'
import useGroups from '@/features/groups/hooks/use-groups.hook'
import MatchingModalContent from '@/features/matching/components/matching-modals/matching-modal-content/matching-modal-content.vue'
import useMatchingInformation from '@/features/matching/hooks/use-matching-information.hook'
import useMatchingSettings from '@/features/matching/hooks/use-matching-settings.hook'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import Modal from '@/features/ui/components/modal/modal.vue'
import {
  selectGetMenteeMatchingModal,
  selectGetMentorMatchingModal,
  selectGetNoMentorFoundModal,
  selectGetShouldHideMatchingModals,
} from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import { ANALYTICS_EVENTS, trackEvent } from '@/lib/posthog'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'

const menteeMatchingModalRef = selectGetMenteeMatchingModal(modalsStore)
const mentorMatchingModalRef = selectGetMentorMatchingModal(modalsStore)
const noMentorFoundModalRef = selectGetNoMentorFoundModal(modalsStore)

const selectedProgrmaIdRef = selectGetSelectedProgramId(programsStore)

const { programRef: selectedProgramRef } = useProgram(selectedProgrmaIdRef)
const { canFillCriteria, canAutoMatch } = useMatchingSettings(selectedProgrmaIdRef)

const { groupsRef, isPendingRef: isGroupsPendingRef } = useGroups()

const shouldHideMatchingModalsRef = selectGetShouldHideMatchingModals(modalsStore)

const { programRolesRef } = useProgramRoles()

const currentRouteRef = selectGetCurrentRoute(routeStore)

// Utiliser le hook pour vérifier les critères mentee spécifiquement
const { menteeCriteriaRef, isMenteeCriteriaCompleteRef, isStaffRef } = useMatchingInformation({
  onSubmitCallback: () => null,
})

// Sans suggestion automatique, le mentee ne peut pas lancer le matching : la
// saisie est obligatoire et la modale n'a pas de sortie (comme côté mentor).
// Exception : admin, manager ou support du programme.
const isMandatoryRef = computed(() => !canAutoMatch.value && !isStaffRef.value)

// Critères « remplis » : tous les champs du template quand la saisie est
// obligatoire (un import partiel ne doit pas passer pour complet), au moins un
// champ en auto-match (règle historique, le mentee relance la recherche lui-même).
const hasMenteeCriteriaRef = computed(() => {
  if (isMandatoryRef.value) return isMenteeCriteriaCompleteRef.value
  return !!menteeCriteriaRef.value && menteeCriteriaRef.value.length > 0
})

// Gérer la fermeture du modal
const handleModalExit = () => {
  // Track abandon si le mentee ferme le modal depuis le slider de confirmation
  if (menteeMatchingModalRef.value?.activeStep === 'matching-confirmation') {
    const mentors = menteeMatchingModalRef.value?.attachedData?.mentors ?? []
    const sliderMountTime = menteeMatchingModalRef.value?.attachedData?.sliderMountTime ?? Date.now()
    trackEvent(ANALYTICS_EVENTS.MATCHING_SELECT_ABANDON, {
      program_id: selectedProgrmaIdRef.value,
      role: 'mentee',
      results_count: mentors.length,
      time_on_page_ms: Date.now() - sliderMountTime,
    })
  }

  // Saisie obligatoire : la modale ne se ferme que sur changement de route
  // (fermeture forcée par le composant Modal) et le watcher la rouvre aussitôt.
  // On ne pose donc jamais le drapeau « masquer » dans ce cas.
  if (isMandatoryRef.value) return

  // Si l'utilisateur a rempli les critères (ou a déjà un groupe via auto-match), ne pas bloquer les autres modaux
  // Sinon, bloquer (car il a cliqué sur "Remplir plus tard" ou fermé sans remplir)
  if (!hasMenteeCriteriaRef.value && groupsRef.value.length === 0) {
    modalsStore.toggleShouldHideMatchingModals(true)
  }
}

// Déterminer les steps du modal selon les options de matching.
// La modale s'ouvre directement sur les champs : l'ancienne étape « rappel
// d'objectif » (description du programme + « Remplir plus tard ») a été fondue
// dans l'écran des critères.
const modalStepsRef = computed(() => {
  // 1. Si les critères ne sont pas activés → pas de steps (la modale n'est jamais ouverte)
  if (!canFillCriteria.value) {
    return []
  }

  // 2. Critères remplis ET auto-match désactivé → pas de steps (l'alert du dashboard gère l'info)
  if (hasMenteeCriteriaRef.value && !canAutoMatch.value) {
    return []
  }

  // 3. Auto-match désactivé ET critères pas encore remplis → permettre de les remplir
  if (!canAutoMatch.value) {
    return [{ component: MatchingModalContent, id: 'fill-criteria', stepName: null }]
  }

  // 4. Auto-match activé (critères remplis ou non) → processus complet de matching
  return [
    { component: MatchingModalContent, id: 'fill-criteria', stepName: null },
    { component: MenteeMatchingLoading, id: 'matching-loading', stepName: null },
    { component: MenteeMatchingConfirmation, id: 'matching-confirmation', stepName: null },
  ]
})

// on mount, if it's a mentee, check if they have a mentorship group for the current program
// if the mentee doesn't have a mentorship group for the current program, show the mentee matching modal
watchEffect(() => {
  if (programRolesRef.value?.includes(RoleEnumTypeV2.MENTEE)) {
    // Si les critères ne sont pas activés, ne jamais ouvrir le modal
    if (!canFillCriteria.value) {
      modalsStore.toggleModal('mentee-matching', false)
      return
    }

    // if it's pending or the user manually select to hide matching modals, close the modal
    if (isGroupsPendingRef.value || shouldHideMatchingModalsRef.value) {
      modalsStore.toggleModal('mentee-matching', false)
      return
    }

    // Check if we're on an admin path - if so, don't open the matching modal
    if (currentRouteRef.value?.path?.startsWith('/admin/')) {
      return
    }

    // Ne pas ouvrir le modal mentee si le modal mentor est ouvert
    // (pour éviter les conflits quand l'utilisateur est mentor ET mentee)
    if (mentorMatchingModalRef.value.isOpen) {
      return
    }

    // Si les critères sont déjà remplis et la modal pas ouverte, ne pas l'ouvrir
    // (si la modal est déjà ouverte, le flow auto-match est en cours → ne pas interrompre)
    if (hasMenteeCriteriaRef.value && !menteeMatchingModalRef.value.isOpen) {
      return
    }

    if (!menteeMatchingModalRef.value.isOpen && groupsRef.value.length === 0 && !noMentorFoundModalRef.value.isOpen) {
      modalsStore.toggleModal('mentee-matching', true)
    } else if (menteeMatchingModalRef.value.isOpen && groupsRef.value.length > 0) {
      modalsStore.toggleModal('mentee-matching', false)
    }
  }
})
</script>

<style lang="scss"></style>
